import { logUnknownException } from '@local/error-logging';
import EmptyState from '@local/web-design-system-2/components/EmptyState/EmptyState';
import TableSkeleton from '@local/web-design-system-2/components/TableSkeleton/TableSkeleton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { observeElementRect, useVirtualizer } from '@tanstack/react-virtual';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';

import { DataTableBodyCell } from './data-table-body-cell/DataTableBodyCell';
import { i18n as bodyCellI18n } from './data-table-body-cell/DataTableBodyCell.i18n';
import { DataTableFilterBar } from './data-table-filter-bar/DataTableFilterBar';
import { DataTableHeaderCell } from './data-table-header-cell/DataTableHeaderCell';
import { i18n } from './DataTable.i18n';
import { useStyles } from './DataTable.styles';
import {
    DEFAULT_DATA_TABLE_STATE,
    SortDirection,
    toCellString,
    type DataTableCellShell,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableQueryState,
    type DataTableRow,
    type DataTableSort,
    type DataTableState,
    type OnRowClick,
} from './DataTable.types';
import { useColumnResize } from './hooks/use-column-resize';

const SKELETON_ROWS = 25; // Generous fixed count — CSS clips during loading
const COPIED_RESET_MS = 2000;
// Default row height fed to the virtualizer before real measurement; matches
// the dense single-line row produced by the table's `size="small"` cells.
const ESTIMATED_ROW_HEIGHT = 37;
// Seeds the virtualizer's viewport before the scroll element is measured —
// required for jsdom (tests), where elements never get a real layout size.
const INITIAL_VIEWPORT_RECT = { width: 1200, height: 800 };

export interface DataTableProps {
    columns: DataTableColDef[];
    rows: DataTableRow[];
    isLoading?: boolean;
    onRowClick?: OnRowClick;
    titleWhenNoResults?: string;
    messageWhenNoResults?: string;
    /**
     * Initial sort applied on first render. Honored only when the table is
     * uncontrolled (i.e. no `queryState` prop).
     */
    initialSort?: { field: string; direction?: SortDirection } | DataTableSort[];

    /**
     * Initial column filters applied on first render, keyed by column field
     * with the list of allowed cell values (e.g. `{ state: ['A'] }`). Honored
     * only when the table is uncontrolled (i.e. no `queryState` prop).
     */
    initialFilters?: Record<string, DataTableCellValue[]>;

    /**
     * When provided, the table switches to controlled mode: sorting,
     * filtering, and searching are delegated to the parent via
     * `onQueryStateChange` instead of happening client-side.
     */
    queryState?: DataTableQueryState;
    onQueryStateChange?: React.Dispatch<React.SetStateAction<DataTableQueryState>>;

    totalRows?: number;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;

    showSearch?: boolean;
    searchPlaceholder?: string;
    /** When provided, renders an expand/collapse chevron per row. The callback
     *  receives the row data and should return the expanded content. */
    renderExpandedRow?: (row: DataTableRow) => ReactNode;
    /** When true, the table sizes to its content instead of filling available height. */
    autoHeight?: boolean;
    /**
     * When set, the matching row (by `row.id`) is visually highlighted and
     * scrolled into view. Cleared automatically when the user changes the
     * table's sort, filter, or search state.
     */
    highlightedRowId?: string | number;
}

const normalizeInitialSort = (input: DataTableProps['initialSort']): DataTableSort[] => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    return [{ field: input.field, direction: input.direction ?? SortDirection.ASC }];
};

const EXPAND_COLUMN_WIDTH = 40;

export const DataTable = ({
    columns,
    rows,
    isLoading = false,
    onRowClick,
    titleWhenNoResults,
    messageWhenNoResults,
    initialSort,
    initialFilters,
    queryState,
    onQueryStateChange,
    totalRows,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    showSearch = false,
    searchPlaceholder,
    renderExpandedRow,
    autoHeight = false,
    highlightedRowId,
}: DataTableProps) => {
    const intl = useIntl();
    const { classes, cx } = useStyles();
    const resolvedNoResultsTitle = titleWhenNoResults ?? intl.formatMessage(i18n.noResultsTitle);
    const resolvedNoResultsMessage =
        messageWhenNoResults ?? intl.formatMessage(i18n.noResultsMessage);
    const copiedLabel = intl.formatMessage(bodyCellI18n.copied);
    const clickToCopyLabel = intl.formatMessage(bodyCellI18n.clickToCopy);

    const isExpandable = !!renderExpandedRow;
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

    const toggleExpand = useCallback((rowId: string | number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    }, []);

    const [activeHighlightId, setActiveHighlightId] = useState<string | number | undefined>(
        highlightedRowId,
    );
    // Tracks whether we've already scrolled to the current highlight so the
    // row isn't yanked back into view on every subsequent render.
    const didScrollToHighlightRef = useRef(false);

    const isControlled = queryState !== undefined;

    const [internalState, setInternalState] = useState<DataTableState>(() => ({
        ...DEFAULT_DATA_TABLE_STATE,
        sorts: normalizeInitialSort(initialSort),
        filters: initialFilters ?? {},
    }));

    const effectiveSorts = queryState ? queryState.sorts : internalState.sorts;
    const effectiveFilters = queryState ? queryState.filters : internalState.filters;
    const effectiveSearch = queryState ? queryState.search : '';

    // Sync prop → state so highlight works when the table is already mounted
    // (e.g. navigating to the same instance-detail page from the search bar).
    useEffect(() => {
        setActiveHighlightId(highlightedRowId);
        didScrollToHighlightRef.current = false;
    }, [highlightedRowId]);

    // Serialised snapshots of the user-interactive dimensions. Keying the clear
    // effect on a string means (a) reference churn from controlled-mode setState
    // creating new array/object identities and (b) React StrictMode's
    // mount→cleanup→remount double-invoke both produce the same string and are
    // correctly skipped — the highlight is only cleared when the user actually
    // changes the sort, filter, or search value.
    const sortsKey = useMemo(() => JSON.stringify(effectiveSorts), [effectiveSorts]);
    const filtersKey = useMemo(() => JSON.stringify(effectiveFilters), [effectiveFilters]);
    const clearKey = `${sortsKey}|${filtersKey}|${effectiveSearch}`;
    const lastClearKeyRef = useRef(clearKey);
    useEffect(() => {
        if (clearKey === lastClearKeyRef.current) return;
        lastClearKeyRef.current = clearKey;
        didScrollToHighlightRef.current = true;
        setActiveHighlightId(undefined);
        // Jump back to the top for the new server-side result set. Must not
        // run on mount: the highlight scroll-to-row may have already
        // positioned the viewport by then.
        if (isControlled) {
            const container = containerRef.current;
            if (container) container.scrollTop = 0;
        }
        // containerRef is a stable MutableRefObject — safe to omit from deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearKey, isControlled]);

    const queryStateRef = useRef<DataTableQueryState>({
        sorts: effectiveSorts,
        filters: effectiveFilters,
        search: effectiveSearch,
    });
    queryStateRef.current = {
        sorts: effectiveSorts,
        filters: effectiveFilters,
        search: effectiveSearch,
    };

    const emit = useCallback(
        (patch: Partial<DataTableQueryState>) => {
            if (isControlled) {
                onQueryStateChange?.((prev) => ({ ...prev, ...patch }));
                return;
            }
            setInternalState((prev) => {
                const next = { ...prev };
                if (patch.sorts !== undefined) next.sorts = patch.sorts;
                if (patch.filters !== undefined) next.filters = patch.filters;
                return next;
            });
        },
        [isControlled, onQueryStateChange],
    );

    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const tableRef = useRef<HTMLTableElement>(null);
    const headerTableRef = useRef<HTMLTableElement>(null);
    const topScrollbarRef = useRef<HTMLDivElement>(null);
    const [scrollContentWidth, setScrollContentWidth] = useState(0);
    const [showTopScrollbar, setShowTopScrollbar] = useState(false);

    const { containerRef, activeResizeField, getColumnWidth, onResizeStart, onResizeHover } =
        useColumnResize(columns, isExpandable ? EXPAND_COLUMN_WIDTH : 0);

    useEffect(() => {
        return () => {
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
            }
        };
    }, []);

    const handleSortSet = useCallback(
        (field: string, direction: SortDirection) => {
            const current = queryStateRef.current.sorts;
            const existing = current.find((s) => s.field === field);
            let nextSorts: DataTableSort[];
            if (existing) {
                nextSorts = current.map((s) => (s.field === field ? { ...s, direction } : s));
            } else {
                nextSorts = [...current, { field, direction }];
            }
            emit({ sorts: nextSorts });
        },
        [emit],
    );

    const handleSortClear = useCallback(
        (field: string) => {
            const current = queryStateRef.current.sorts;
            emit({ sorts: current.filter((s) => s.field !== field) });
        },
        [emit],
    );

    const handleCopy = useCallback(async (key: string, value: DataTableCellValue) => {
        try {
            await navigator.clipboard.writeText(toCellString(value));
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
            }
            setCopiedKey(key);
            copiedTimerRef.current = setTimeout(() => {
                setCopiedKey(null);
                copiedTimerRef.current = null;
            }, COPIED_RESET_MS);
        } catch (error) {
            logUnknownException(error);
        }
    }, []);

    const handleFilterChange = useCallback(
        (field: string, values: DataTableCellValue[]) => {
            const column = columns.find((c) => c.field === field);
            const optionCount = column?.filterOptions?.length ?? 0;
            const nextFilters = { ...queryStateRef.current.filters };
            if (optionCount > 0 && values.length === optionCount) {
                delete nextFilters[field];
            } else {
                nextFilters[field] = values;
            }
            emit({ filters: nextFilters });
        },
        [columns, emit],
    );

    const handleSearchChange = useCallback(
        (next: string) => {
            emit({ search: next });
        },
        [emit],
    );

    const filteredRows = useMemo<DataTableRow[]>(() => {
        const filterEntries = Object.entries(effectiveFilters);
        if (filterEntries.length === 0) return rows;

        const normalize = (v: DataTableCellValue): string =>
            v === null || v === undefined ? '' : String(v);
        const normalizedFilters: [string, Set<string>][] = filterEntries.map(([field, vals]) => [
            field,
            new Set(vals.map(normalize)),
        ]);

        return rows.filter((row) =>
            normalizedFilters.every(
                ([field, vals]) => vals.size > 0 && vals.has(normalize(row[field])),
            ),
        );
    }, [rows, effectiveFilters]);

    const sortedRows = useMemo<DataTableRow[]>(() => {
        if (isControlled) return filteredRows;
        if (effectiveSorts.length === 0) return filteredRows;

        return [...filteredRows].sort((a, b) => {
            for (const { field, direction } of effectiveSorts) {
                const aStr = toCellString(a[field]);
                const bStr = toCellString(b[field]);
                const cmp = aStr.localeCompare(bStr, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
                if (cmp !== 0) return direction === SortDirection.ASC ? cmp : -cmp;
            }
            return 0;
        });
    }, [isControlled, filteredRows, effectiveSorts]);

    // Windows the row list so only the visible slice (plus overscan) is
    // mounted — without this, every cached page accumulated by infinite
    // scroll re-renders as real DOM on each navigation back to the page.
    // Each virtual item is a <tbody> holding the row plus its optional
    // expanded-content row, so dynamic measurement captures both.
    const rowVirtualizer = useVirtualizer({
        count: sortedRows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => ESTIMATED_ROW_HEIGHT,
        overscan: 10,
        getItemKey: (index) => sortedRows[index].id,
        initialRect: INITIAL_VIEWPORT_RECT,
        // A 0-height scroll element (jsdom in tests, or pre-layout flex
        // containers) nulls the virtual range and renders no rows — keep the
        // seed rect until a real height is observed.
        observeElementRect: (instance, cb) =>
            observeElementRect(instance, (rect) =>
                cb(rect.height > 0 ? rect : INITIAL_VIEWPORT_RECT),
            ),
    });

    const virtualRows = rowVirtualizer.getVirtualItems();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom =
        virtualRows.length > 0
            ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
            : 0;

    // Scroll the highlighted row into view once its data has loaded. The row
    // may not be mounted (virtualized out), so this goes through the
    // virtualizer rather than a DOM ref, scrolling only the table's own body
    // viewport — never ancestor scroll containers.
    useEffect(() => {
        if (activeHighlightId == null || didScrollToHighlightRef.current) return;
        const index = sortedRows.findIndex((row) => row.id === activeHighlightId);
        if (index < 0) return;
        didScrollToHighlightRef.current = true;
        rowVirtualizer.scrollToIndex(index, { align: 'center' });
    }, [activeHighlightId, sortedRows, rowVirtualizer]);

    useLayoutEffect(() => {
        const table = tableRef.current;
        const container = containerRef.current;
        if (!table || !container) return;
        const measure = () => {
            // Use getBoundingClientRect for subpixel-accurate widths at non-100% zoom
            const tableWidth = table.getBoundingClientRect().width;
            const containerWidth = container.getBoundingClientRect().width;
            // Add 1px buffer to the inner scrollbar div to guarantee scroll travel
            setScrollContentWidth(Math.ceil(tableWidth) + 1);
            setShowTopScrollbar(tableWidth - containerWidth > 1);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(table);
        ro.observe(container);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const topBar = topScrollbarRef.current;
        const bodyTable = tableRef.current;
        if (!topBar || !bodyTable) return;
        const onTopScroll = () => {
            const x = `translateX(-${topBar.scrollLeft}px)`;
            bodyTable.style.transform = x;
            const headerTable = headerTableRef.current;
            if (headerTable) headerTable.style.transform = x;
        };
        topBar.addEventListener('scroll', onTopScroll, { passive: true });
        return () => topBar.removeEventListener('scroll', onTopScroll);
    }, [showTopScrollbar]);

    useEffect(() => {
        const container = containerRef.current;
        const topBar = topScrollbarRef.current;
        if (!container || !topBar) return;
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) return;
            let deltaX = e.deltaX;
            if (e.shiftKey && deltaX === 0) deltaX = e.deltaY;
            if (Math.abs(deltaX) < 1) return;
            if (e.deltaMode === 1) deltaX *= 16;
            else if (e.deltaMode === 2) deltaX *= 100;
            const { scrollLeft, scrollWidth, clientWidth } = topBar;
            const atLeft = scrollLeft <= 0 && deltaX < 0;
            const atRight = scrollLeft >= scrollWidth - clientWidth - 1 && deltaX > 0;
            if (atLeft || atRight) return;
            e.preventDefault();
            topBar.scrollLeft += deltaX;
        };
        container.addEventListener('wheel', onWheel, { passive: false });
        return () => container.removeEventListener('wheel', onWheel);
    }, [showTopScrollbar]);

    const { containerRef: scrollContainerRef, onScroll: handleInfiniteScroll } = useInfiniteScroll({
        hasNextPage: !!hasMore,
        isFetchingNextPage: !!isLoadingMore,
        fetchNextPage: () => onLoadMore?.(),
    });

    // Skeleton only when truly empty + loading (no stale-data flash on refetch).
    const showSkeleton = isLoading && sortedRows.length === 0;
    const isEmpty = !showSkeleton && sortedRows.length === 0;

    const shellByField = useMemo(() => {
        const map: Record<string, DataTableCellShell> = {};
        columns.forEach((column, index) => {
            map[column.field] = {
                column,
                isFirst: index === 0,
                isLast: index === columns.length - 1,
                width: getColumnWidth(column),
                isResizeActive: activeResizeField === column.field,
                onResizeStart,
                onResizeHover,
            };
        });
        return map;
    }, [columns, getColumnWidth, activeResizeField, onResizeStart, onResizeHover]);

    // table-layout: fixed derives column widths from the first row's cells;
    // the virtualizer's spacer rows have no cells, so once one becomes the
    // first row the widths collapse. An explicit <colgroup> shared by the
    // header and body tables pins the widths independently of row content.
    const colGroup = (
        <colgroup>
            {isExpandable && <col style={{ width: EXPAND_COLUMN_WIDTH }} />}
            {columns.map((column) => (
                <col key={column.field} style={{ width: getColumnWidth(column) }} />
            ))}
        </colgroup>
    );

    return (
        <Box className={cx(classes.root, autoHeight && classes.rootAutoHeight)}>
            <DataTableFilterBar
                showSearch={showSearch}
                searchValue={effectiveSearch}
                onSearchChange={handleSearchChange}
                searchPlaceholder={searchPlaceholder}
            />
            {showTopScrollbar && !showSkeleton && (
                <div ref={topScrollbarRef} className={classes.topScrollbar}>
                    <div style={{ width: scrollContentWidth }} />
                </div>
            )}
            <Box
                className={cx(
                    classes.tableScrollArea,
                    autoHeight && classes.tableScrollAreaAutoHeight,
                )}
            >
                {/* Header sits outside the scroll container so the vertical
                    scrollbar starts below it. Both tables share column widths
                    via `shellByField` so the rows align with the header. */}
                <Box
                    className={cx(
                        classes.headerViewport,
                        showTopScrollbar && !showSkeleton && classes.headerViewportFlat,
                    )}
                >
                    <Table ref={headerTableRef} size="small" className={classes.table}>
                        {colGroup}
                        <TableHead>
                            <TableRow>
                                {isExpandable && (
                                    <TableCell
                                        className={cx(
                                            classes.cell,
                                            classes.headerCell,
                                            classes.expandCell,
                                            classes.expandCellSize,
                                        )}
                                    />
                                )}
                                {columns.map((column) => {
                                    const sort = effectiveSorts.find(
                                        (s) => s.field === column.field,
                                    );
                                    const colFilterValues = effectiveFilters[column.field];
                                    const isFiltered =
                                        colFilterValues !== undefined &&
                                        colFilterValues.length !==
                                            (column.filterOptions?.length ?? 0);
                                    return (
                                        <DataTableHeaderCell
                                            key={column.field}
                                            shell={shellByField[column.field]}
                                            isSortActive={sort !== undefined}
                                            sortDirection={sort?.direction ?? SortDirection.ASC}
                                            onSortSet={handleSortSet}
                                            onSortClear={handleSortClear}
                                            filterOptions={column.filterOptions}
                                            filterValues={colFilterValues}
                                            isFilterActive={isFiltered}
                                            onFilterChange={handleFilterChange}
                                        />
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                    </Table>
                </Box>
                <Box
                    className={cx(
                        classes.bodyBorderWrap,
                        autoHeight && classes.bodyBorderWrapAutoHeight,
                    )}
                >
                    <TableContainer
                        ref={(node: HTMLDivElement | null) => {
                            containerRef.current = node;
                            scrollContainerRef(node);
                        }}
                        onScroll={handleInfiniteScroll}
                        className={cx(
                            classes.bodyViewport,
                            showSkeleton && classes.bodyViewportLoading,
                        )}
                    >
                        <Table ref={tableRef} size="small" className={classes.table}>
                            {colGroup}
                            {(showSkeleton || isEmpty) && (
                                <TableBody>
                                    {showSkeleton && (
                                        <TableSkeleton
                                            rows={SKELETON_ROWS}
                                            columns={columns.length + (isExpandable ? 1 : 0)}
                                        />
                                    )}
                                    {isEmpty && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={columns.length + (isExpandable ? 1 : 0)}
                                                className={classes.emptyStateCell}
                                            >
                                                <EmptyState
                                                    title={resolvedNoResultsTitle}
                                                    message={resolvedNoResultsMessage}
                                                    variant="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            )}
                            {!showSkeleton && paddingTop > 0 && (
                                <tbody aria-hidden="true">
                                    <tr>
                                        <td
                                            colSpan={columns.length + (isExpandable ? 1 : 0)}
                                            style={{
                                                height: paddingTop,
                                                padding: 0,
                                                border: 0,
                                            }}
                                        />
                                    </tr>
                                </tbody>
                            )}
                            {!showSkeleton &&
                                virtualRows.map((virtualRow) => {
                                    const row = sortedRows[virtualRow.index];
                                    const isExpanded = expandedRows.has(row.id);
                                    const isHighlighted =
                                        activeHighlightId != null && row.id === activeHighlightId;
                                    return (
                                        <TableBody
                                            key={row.id}
                                            data-index={virtualRow.index}
                                            ref={rowVirtualizer.measureElement}
                                        >
                                            <TableRow
                                                className={cx(
                                                    classes.row,
                                                    !!onRowClick &&
                                                        !isExpandable &&
                                                        classes.rowClickable,
                                                    isExpandable && classes.rowExpandable,
                                                    isExpanded && classes.rowExpanded,
                                                    isHighlighted && classes.rowHighlighted,
                                                )}
                                                onClick={
                                                    isExpandable
                                                        ? () => toggleExpand(row.id)
                                                        : undefined
                                                }
                                            >
                                                {isExpandable && (
                                                    <TableCell
                                                        className={cx(
                                                            classes.cell,
                                                            classes.expandCell,
                                                            classes.expandCellSize,
                                                        )}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            className={cx(
                                                                classes.expandIcon,
                                                                isExpanded &&
                                                                    classes.expandIconOpen,
                                                            )}
                                                            aria-expanded={isExpanded}
                                                            aria-label={intl.formatMessage(
                                                                isExpanded
                                                                    ? i18n.collapseRow
                                                                    : i18n.expandRow,
                                                            )}
                                                        >
                                                            <ChevronRightIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                                {columns.map((column) => {
                                                    const cellKey = `${row.id}-${column.field}`;
                                                    return (
                                                        <DataTableBodyCell
                                                            key={cellKey}
                                                            shell={shellByField[column.field]}
                                                            row={row}
                                                            cellKey={cellKey}
                                                            isCopied={copiedKey === cellKey}
                                                            copiedLabel={copiedLabel}
                                                            clickToCopyLabel={clickToCopyLabel}
                                                            onCopy={handleCopy}
                                                            onRowClick={
                                                                isExpandable
                                                                    ? undefined
                                                                    : onRowClick
                                                            }
                                                        />
                                                    );
                                                })}
                                            </TableRow>
                                            {isExpandable && isExpanded && (
                                                <TableRow className={classes.expandedContentRow}>
                                                    <TableCell
                                                        colSpan={columns.length + 1}
                                                        className={classes.expandedContentCell}
                                                    >
                                                        <Box className={classes.expandedContent}>
                                                            {renderExpandedRow?.(row)}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    );
                                })}
                            {!showSkeleton && paddingBottom > 0 && (
                                <tbody aria-hidden="true">
                                    <tr>
                                        <td
                                            colSpan={columns.length + (isExpandable ? 1 : 0)}
                                            style={{
                                                height: paddingBottom,
                                                padding: 0,
                                                border: 0,
                                            }}
                                        />
                                    </tr>
                                </tbody>
                            )}
                            {isLoadingMore && hasMore && (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length + (isExpandable ? 1 : 0)}
                                            className={classes.loadingMoreCell}
                                        >
                                            <Box className={classes.loadingMoreInner}>
                                                <CircularProgress size={16} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
            {isControlled && totalRows !== undefined && sortedRows.length > 0 && (
                <Box className={classes.tableFooter}>
                    <Box className={classes.totalLabel} automation-id="table-total-label">
                        {intl.formatMessage(i18n.loadedOfTotal, {
                            loaded: sortedRows.length,
                            total: totalRows,
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
